import { useState, useEffect, useRef, useCallback } from "react";
import { PlusIcon, TrashIcon, SparklesIcon, ChevronDownIcon, ChevronRightIcon, ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import { bladesApi } from "../../api/blades";
import { useLookups } from "../../hooks/useLookups";
import Button from "../../components/ui/Button";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";
import Modal from "../../components/ui/Modal";
import MeridContour from "../../components/charts/MeridContour";
import MecoParamDiagram from "../../components/charts/MecoParamDiagram";
import DiffusorParamDiagram from "../../components/charts/DiffusorParamDiagram";
import {
  polylineFromMecoSpline,
  bezierControlPoints,
  interpolateMecoParams,
  polylineFromDiffusorSpline,
  interpolateDiffusorParams,
} from "../../utils/bspline";

import { useChartColors, getChartColors } from "../../chartTheme";

const DEFAULT_LINES = [
  { name: "HUB",     sort_order: 0, active: true, r_coords: [], z_coords: [], theta_start: 0, meco_mode: "polyline", meco_spline_params: null },
  { name: "SL-1", sort_order: 1, active: true, r_coords: [], z_coords: [], theta_start: 0, meco_mode: "polyline", meco_spline_params: null },
  { name: "SL-2", sort_order: 2, active: true, r_coords: [], z_coords: [], theta_start: 0, meco_mode: "polyline", meco_spline_params: null },
  { name: "SL-3", sort_order: 3, active: true, r_coords: [], z_coords: [], theta_start: 0, meco_mode: "polyline", meco_spline_params: null },
  { name: "SHROUD",  sort_order: 4, active: true, r_coords: [], z_coords: [], theta_start: 0, meco_mode: "polyline", meco_spline_params: null },
];

const EMPTY_SPLINE = {
  le_r: "", le_z: "", inlet_z_start: "",
  l1: "", alpha_le: 0,
  l2: "", alpha_te: 0,
  te_r: "", te_z: "", l_out: "",
};

const EMPTY_DIFFUSOR = {
  R3: "", Z3: "", R4: "", gamma4: 0,
  R5: "", Z5: "", Z5b: "",
  R6: "", Z6: "", R7: "", gamma7: 0,
  R8: "", Z8: "", L9: "",
};

const DIFFUSOR_PARAM_SECTIONS = [
  {
    title: "Inlet",
    rows: [
      { key: "R3",     label: "R3",  sub: "inlet start radius",              unit: "mm" },
      { key: "Z3",     label: "Z3",  sub: "inlet start axial pos.",          unit: "mm" },
      { key: "R4",     label: "R4",  sub: "arc-start radius",                unit: "mm" },
      { key: "gamma4", label: "Γ4",  sub: "inlet tangent angle from radial", unit: "°"  },
    ],
  },
  {
    title: "Outer Apex",
    rows: [
      { key: "R5",  label: "R5",  sub: "apex outer radius",           unit: "mm" },
      { key: "Z5",  label: "Z5",  sub: "upper apex axial coordinate", unit: "mm" },
      { key: "Z5b", label: "Z5b", sub: "lower apex axial coordinate", unit: "mm" },
    ],
  },
  {
    title: "Return",
    rows: [
      { key: "R6",     label: "R6",  sub: "return tangent anchor radius",    unit: "mm" },
      { key: "Z6",     label: "Z6",  sub: "return tangent anchor axial pos.",unit: "mm" },
      { key: "R7",     label: "R7",  sub: "return straight end radius",      unit: "mm" },
      { key: "gamma7", label: "Γ7",  sub: "return tangent angle from radial",unit: "°"  },
    ],
  },
  {
    title: "Outlet",
    rows: [
      { key: "R8", label: "R8", sub: "outlet radius",          unit: "mm" },
      { key: "Z8", label: "Z8", sub: "outlet axial pos.",      unit: "mm" },
      { key: "L9", label: "L9", sub: "outlet straight length", unit: "mm" },
    ],
  },
];

// ── Chart data helpers ────────────────────────────────────────────────────────

function resolveChartData(lines) {
  const { LINE_COLORS } = getChartColors();
  return lines.map((l, i) => {
    const color = LINE_COLORS[i % LINE_COLORS.length];
    if ((!l.r_coords || !l.r_coords.length) && l.meco_spline_params) {
      const { r, z } = polylineFromMecoSpline(l.meco_spline_params);
      return { name: l.name, r, z, color };
    }
    return { name: l.name, r: l.r_coords || [], z: l.z_coords || [], color };
  });
}

// ── Coordinate table ──────────────────────────────────────────────────────────

function CoordTable({ rCoords, zCoords, onChange, readOnly = false }) {
  const [copied, setCopied] = useState(false);
  const len = Math.max(rCoords.length, zCoords.length, 1);
  const rows = Array.from({ length: len }, (_, i) => ({
    r: rCoords[i] ?? "", z: zCoords[i] ?? "",
  }));

  const emit = (newRows) => onChange(
    newRows.map(r => parseFloat(r.r)).filter(v => !isNaN(v)),
    newRows.map(r => parseFloat(r.z)).filter(v => !isNaN(v)),
  );

  const updateCell = (rowIdx, col, val) =>
    emit(rows.map((r, i) => i === rowIdx ? { ...r, [col]: val } : r));
  const addRow    = () => emit([...rows, { r: "", z: "" }]);
  const deleteRow = (i) => emit(rows.filter((_, j) => j !== i));

  const copyToClipboard = useCallback(() => {
    const text = rows
      .filter(r => r.r !== "" || r.z !== "")
      .map(r => `${r.r}\t${r.z}`)
      .join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [rows]);

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const parsed = text.trim().split(/\r?\n/).map(line => {
      const parts = line.split("\t");
      return { r: parts[0]?.replace(",", ".") ?? "", z: parts[1]?.replace(",", ".") ?? "" };
    }).filter(r => r.r !== "" || r.z !== "");
    if (parsed.length) emit(parsed);
  }, [emit]);

  return (
    <div onPaste={handlePaste}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="w-6 border border-gray-200 px-1 py-1 text-gray-400 font-normal">#</th>
              <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">r [mm]</th>
              <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">z [mm]</th>
              <th className="w-6 border border-gray-200 px-1 text-center">
                <button
                  onClick={copyToClipboard}
                  title="Copy table to clipboard"
                  className="text-gray-300 hover:text-bh-600 transition-colors"
                >
                  {copied
                    ? <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 text-bh-500" />
                    : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-bh-50/40">
                <td className="border border-gray-200 px-1 py-0.5 text-gray-300 text-center select-none">{i + 1}</td>
                <td className="border border-gray-200 p-0">
                  <input type="number" step="any" value={row.r}
                    onChange={e => updateCell(i, "r", e.target.value)} disabled={readOnly}
                    className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50 disabled:text-gray-400" />
                </td>
                <td className="border border-gray-200 p-0">
                  <input type="number" step="any" value={row.z}
                    onChange={e => updateCell(i, "z", e.target.value)} disabled={readOnly}
                    className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50 disabled:text-gray-400" />
                </td>
                <td className="border border-gray-200 px-1 text-center">
                  <button onClick={() => deleteRow(i)} disabled={readOnly}
                    className="text-gray-300 hover:text-red-400 transition-colors disabled:opacity-30">
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button onClick={addRow}
          className="mt-1 flex items-center gap-1 text-xs text-bh-500 hover:text-bh-700 px-1">
          <PlusIcon className="h-3 w-3" /> Add row
        </button>
      )}
      <p className="text-gray-400 text-xs mt-1 px-1">Copy table header icon → paste with Ctrl+V in another table</p>
    </div>
  );
}

// ── Assistant modal ───────────────────────────────────────────────────────────

const PARAM_SECTIONS = [
  {
    title: "Inlet (Leading Edge)",
    rows: [
      { key: "le_r",          label: "r_LE",  sub: "radius at LE",              unit: "mm" },
      { key: "le_z",          label: "z_LE",  sub: "axial pos. at LE",          unit: "mm" },
      { key: "inlet_z_start", label: "z_IN",  sub: "z-start of inlet straight", unit: "mm" },
    ],
  },
  {
    title: "Bezier",
    rows: [
      { key: "l1",       label: "L1",   sub: "tangent length at LE",    unit: "mm" },
      { key: "alpha_le", label: "α_LE", sub: "angle from axial at LE",  unit: "°" },
      { key: "l2",       label: "L2",   sub: "tangent length at TE",    unit: "mm" },
      { key: "alpha_te", label: "α_TE", sub: "angle from radial at TE", unit: "°" },
    ],
  },
  {
    title: "Outlet (Trailing Edge)",
    rows: [
      { key: "te_r",  label: "r_TE",  sub: "radius at TE",          unit: "mm" },
      { key: "te_z",  label: "z_TE",  sub: "axial pos. at TE",      unit: "mm" },
      { key: "l_out", label: "L_OUT", sub: "outlet straight length", unit: "mm" },
    ],
  },
];

function ParamCell({ value, onChange, unit, readOnly }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <input
        type="number" step="any" value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        disabled={readOnly}
        className="w-[60px] text-xs text-right border border-gray-200 rounded px-1 py-0.5
          focus:outline-none focus:border-bh-400 bg-white disabled:bg-gray-50 disabled:text-gray-400"
      />
      <span className="text-xs text-gray-400 w-5 text-left">{unit}</span>
    </div>
  );
}

function ControlPointsDisplay({ label, params, color }) {
  if (!params) return null;
  const { le_r, le_z, l1, l2, te_r, te_z } = params;
  const hasAll = [le_r, le_z, l1, l2, te_r, te_z].every(v => v != null && v !== "");
  if (!hasAll) return null;

  const cps = bezierControlPoints({
    ...params,
    alpha_le: params.alpha_le ?? 0,
    alpha_te: params.alpha_te ?? 0,
  });
  const fmt = v => v.toFixed(2);

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color }}>
        {label}
      </p>
      {cps.map(([r, z], i) => (
        <div key={i} className="flex gap-2 text-xs py-0.5">
          <span className="font-medium text-gray-600 w-5">P{i}</span>
          <span className="text-gray-500">r = {fmt(r)}</span>
          <span className="text-gray-500">z = {fmt(z)}</span>
        </div>
      ))}
    </div>
  );
}

function buildAssistantTraces(hubP, shrP, lines) {
  const { LINE_COLORS } = getChartColors();
  const traces = [];
  const valid = (p) => p && [p.le_r, p.le_z, p.l1, p.l2, p.te_r, p.te_z].every(v => v != null && v !== "");

  const addSurface = (params, name, color) => {
    const { r, z } = polylineFromMecoSpline(params);
    if (r.length) {
      traces.push({
        type: "scatter", mode: "lines", name,
        x: r, y: z,
        line: { color, width: 2.5 },
        showlegend: true,
      });
    }
    if (!valid(params)) return;
    const [P0, P1, P2, P3] = bezierControlPoints({
      ...params, alpha_le: params.alpha_le ?? 0, alpha_te: params.alpha_te ?? 0,
    });
    // Tangent handles
    traces.push({
      type: "scatter", mode: "lines", showlegend: false,
      x: [P0[0], P1[0]], y: [P0[1], P1[1]],
      line: { color, width: 1.2, dash: "dot" },
    });
    traces.push({
      type: "scatter", mode: "lines", showlegend: false,
      x: [P2[0], P3[0]], y: [P2[1], P3[1]],
      line: { color, width: 1.2, dash: "dot" },
    });
    // Control point markers
    traces.push({
      type: "scatter", mode: "markers", showlegend: false,
      x: [P0[0], P1[0], P2[0], P3[0]],
      y: [P0[1], P1[1], P2[1], P3[1]],
      text: ["P0 (LE)", "P1", "P2", "P3 (TE)"],
      marker: {
        symbol: ["circle", "diamond", "diamond", "circle"],
        size: [9, 8, 8, 9],
        color: ["white", color, color, "white"],
        line: { color, width: 2 },
      },
      hovertemplate: `%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>${name}</extra>`,
    });
  };

  // Intermediate SLs (dotted, lighter)
  lines.forEach((line, i) => {
    if (i === 0 || i === lines.length - 1) return;
    const t = i / (lines.length - 1);
    const params = interpolateMecoParams(hubP, shrP, t);
    const { r, z } = polylineFromMecoSpline(params);
    if (!r.length) return;
    traces.push({
      type: "scatter", mode: "lines", name: line.name, showlegend: false,
      x: r, y: z,
      line: { color: LINE_COLORS[i % LINE_COLORS.length], width: 1.5, dash: "dot" },
      opacity: 0.55,
    });
  });

  addSurface(hubP,  "HUB",    LINE_COLORS[0]);
  addSurface(shrP,  "SHROUD", LINE_COLORS[LINE_COLORS.length - 1]);

  return traces;
}

function AssistantChart({ hubParams, shroudParams, lines }) {
  const traces = buildAssistantTraces(hubParams, shroudParams, lines);
  return (
    <Plot
      data={traces}
      layout={{
        margin: { l: 55, r: 14, t: 10, b: 45 },
        xaxis: { title: "r [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot" },
        yaxis: { title: "z [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot", scaleanchor: "x", scaleratio: 1 },
        paper_bgcolor: "transparent",
        plot_bgcolor: "#f9fafb",
        showlegend: true,
        legend: { font: { size: 10 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
        hovermode: "closest",
        dragmode: "pan",
      }}
      config={{ displayModeBar: "hover", modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]], scrollZoom: true, responsive: true, displaylogo: false }}
      style={{ width: "100%", height: "100%" }}
      useResizeHandler
    />
  );
}

function MecoAssistantModal({ open, onClose, lines, savedAssistantParams, onApply, isLocked }) {
  const { LINE_COLORS } = useChartColors();
  const [hubParams,    setHubParams]    = useState(EMPTY_SPLINE);
  const [shroudParams, setShroudParams] = useState(EMPTY_SPLINE);
  const [showDiagram,  setShowDiagram]  = useState(false);

  useEffect(() => {
    if (!open) return;
    // Priority: persisted assistant params > per-line spline params > empty
    setHubParams({
      ...EMPTY_SPLINE,
      ...(lines[0]?.meco_spline_params || {}),
      ...(savedAssistantParams?.hub || {}),
    });
    setShroudParams({
      ...EMPTY_SPLINE,
      ...(lines[lines.length - 1]?.meco_spline_params || {}),
      ...(savedAssistantParams?.shroud || {}),
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    const newLines = lines.map((line, i) => {
      const t = lines.length > 1 ? i / (lines.length - 1) : 0;
      const params = i === 0 ? hubParams
                   : i === lines.length - 1 ? shroudParams
                   : interpolateMecoParams(hubParams, shroudParams, t);
      const { r, z } = polylineFromMecoSpline(params);
      return { ...line, r_coords: r, z_coords: z, meco_spline_params: params, meco_mode: "polyline" };
    });
    onApply(newLines, { hub: hubParams, shroud: shroudParams });
  };

  const setHub    = (key) => (val) => setHubParams(p => ({ ...p, [key]: val }));
  const setShroud = (key) => (val) => setShroudParams(p => ({ ...p, [key]: val }));

  return (
    <Modal open={open} onClose={onClose} title="B-Spline MECO Assistant" size="2xl">
      <div className="flex flex-col" style={{ height: "600px" }}>

        {/* ── Main area ── */}
        <div className="flex flex-1 min-h-0 -mx-6 border-y border-gray-100">

          {/* Param table */}
          <div className="w-[460px] shrink-0 border-r border-gray-200 overflow-y-auto p-4 space-y-4">

            {/* Column headers */}
            <div className="grid grid-cols-[1fr_88px_88px] gap-x-3 px-1 pb-1 border-b border-gray-200">
              <div />
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LINE_COLORS[0] }} />
                <span className="text-xs font-bold" style={{ color: LINE_COLORS[0] }}>HUB</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LINE_COLORS[LINE_COLORS.length - 1] }} />
                <span className="text-xs font-bold" style={{ color: LINE_COLORS[LINE_COLORS.length - 1] }}>SHROUD</span>
              </div>
            </div>

            {/* Parameter diagram toggle */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowDiagram(s => !s)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span>Parameter diagram</span>
                {showDiagram
                  ? <ChevronDownIcon className="h-3.5 w-3.5" />
                  : <ChevronRightIcon className="h-3.5 w-3.5" />}
              </button>
              {showDiagram && (
                <div className="px-2 py-2 bg-white">
                  <MecoParamDiagram />
                </div>
              )}
            </div>

            {/* Param sections */}
            {PARAM_SECTIONS.map(({ title, rows }) => (
              <div key={title}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  {title}
                </p>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  {rows.map(({ key, label, sub, unit }) => (
                    <div key={key}
                      className="grid grid-cols-[1fr_88px_88px] gap-x-3 px-3 py-1.5 border-b border-gray-100 last:border-0 items-center">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700">{label}</p>
                        <p className="text-xs text-gray-400 leading-tight">{sub}</p>
                      </div>
                      <ParamCell value={hubParams[key]} onChange={setHub(key)} unit={unit} readOnly={isLocked} />
                      <ParamCell value={shroudParams[key]} onChange={setShroud(key)} unit={unit} readOnly={isLocked} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Control points display */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Bezier Control Points
              </p>
              <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-3">
                <ControlPointsDisplay label="Hub" params={hubParams} color={LINE_COLORS[0]} />
                <ControlPointsDisplay label="Shroud" params={shroudParams} color={LINE_COLORS[LINE_COLORS.length - 1]} />
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 min-w-0 p-3 flex flex-col">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 shrink-0">
              Live preview — ○ P0/P3 on curve · ◆ P1/P2 control points · ‥ tangent handles
            </p>
            <div className="flex-1 min-h-0">
              <AssistantChart
                hubParams={hubParams}
                shroudParams={shroudParams}
                lines={lines}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 shrink-0">
          <p className="text-xs text-gray-400">
            Applies computed r/z points to every stream surface (SLs interpolated).
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleApply} disabled={isLocked}>Apply to all lines</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Radial Diffusor Channel assistant ────────────────────────────────────────

function buildDiffusorAssistantTraces(hubP, shrP, lines) {
  const { LINE_COLORS } = getChartColors();
  const traces = [];
  const REQUIRED = ["R3","Z3","R4","gamma4","R5","Z5","Z5b","R6","Z6","R7","gamma7","R8","Z8","L9"];
  const ok = p => p && REQUIRED.every(k => p[k] != null && p[k] !== "");

  const addSurface = (params, name, color) => {
    const { r, z } = polylineFromDiffusorSpline(params);
    if (!r.length) return;
    traces.push({
      type: "scatter", mode: "lines", name,
      x: r, y: z,
      line: { color, width: 2.5 },
      showlegend: true,
    });
    if (!ok(params)) return;

    const g4 = (+params.gamma4 * Math.PI) / 180;
    const g7 = (+params.gamma7 * Math.PI) / 180;
    const z_in  = r => +params.Z3 + (+r - +params.R3) * Math.tan(g4);
    const z_ret = r => +params.Z6 - (+r - +params.R6) * Math.tan(g7);

    const P3   = [+params.R3, +params.Z3];
    const P4   = [+params.R4, z_in(params.R4)];
    const CP34 = [+params.R5, z_in(params.R5)];
    const P5a  = [+params.R5, +params.Z5];    // direct upper apex
    const P5b  = [+params.R5, +params.Z5b];  // direct lower apex
    const CP56 = [+params.R5, z_ret(params.R5)];
    const P6   = [+params.R6, +params.Z6];
    const P7   = [+params.R7, z_ret(params.R7)];
    const CP78 = [+params.R8, z_ret(params.R8)];
    const P8   = [+params.R8, +params.Z8];

    // Tangent handle lines
    [[P4, CP34], [CP34, P5a], [P5b, CP56], [CP56, P6], [P7, CP78], [CP78, P8]].forEach(([A, B]) => {
      traces.push({
        type: "scatter", mode: "lines", showlegend: false,
        x: [A[0], B[0]], y: [A[1], B[1]],
        line: { color, width: 1.2, dash: "dot" },
      });
    });

    // Anchor points (on curve)
    const anchors = [P3, P4, P5a, P5b, P6, P7, P8];
    const anchorLabels = ["P3 (inlet)", "P4", "P5a (apex top)", "P5b (apex bot)", "P6", "P7", "P8 (outlet)"];
    traces.push({
      type: "scatter", mode: "markers", showlegend: false,
      x: anchors.map(p => p[0]), y: anchors.map(p => p[1]),
      text: anchorLabels,
      marker: { symbol: "circle", size: 8, color: "white", line: { color, width: 2 } },
      hovertemplate: `%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>${name}</extra>`,
    });

    // Control points (off curve)
    traces.push({
      type: "scatter", mode: "markers", showlegend: false,
      x: [CP34[0], CP56[0], CP78[0]], y: [CP34[1], CP56[1], CP78[1]],
      text: ["CP34", "CP56", "CP78"],
      marker: { symbol: "diamond", size: 7, color, line: { color, width: 1 } },
      hovertemplate: `%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>${name}</extra>`,
    });
  };

  // Intermediate SLs (dotted)
  lines.forEach((line, i) => {
    if (i === 0 || i === lines.length - 1) return;
    const t = i / (lines.length - 1);
    const params = interpolateDiffusorParams(hubP, shrP, t);
    const { r, z } = polylineFromDiffusorSpline(params);
    if (!r.length) return;
    traces.push({
      type: "scatter", mode: "lines", name: line.name, showlegend: false,
      x: r, y: z,
      line: { color: LINE_COLORS[i % LINE_COLORS.length], width: 1.5, dash: "dot" },
      opacity: 0.55,
    });
  });

  addSurface(hubP, "HUB",    LINE_COLORS[0]);
  addSurface(shrP, "SHROUD", LINE_COLORS[LINE_COLORS.length - 1]);
  return traces;
}

function RadialDiffusorAssistantModal({ open, onClose, lines, savedAssistantParams, onApply, isLocked }) {
  const { LINE_COLORS } = useChartColors();
  const [hubParams,    setHubParams]    = useState(EMPTY_DIFFUSOR);
  const [shroudParams, setShroudParams] = useState(EMPTY_DIFFUSOR);
  const [showDiagram,  setShowDiagram]  = useState(false);

  useEffect(() => {
    if (!open) return;
    setHubParams({ ...EMPTY_DIFFUSOR, ...(savedAssistantParams?.hub || {}) });
    setShroudParams({ ...EMPTY_DIFFUSOR, ...(savedAssistantParams?.shroud || {}) });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    const newLines = lines.map((line, i) => {
      const t = lines.length > 1 ? i / (lines.length - 1) : 0;
      const params = i === 0 ? hubParams
                   : i === lines.length - 1 ? shroudParams
                   : interpolateDiffusorParams(hubParams, shroudParams, t);
      const { r, z } = polylineFromDiffusorSpline(params);
      return { ...line, r_coords: r, z_coords: z, meco_mode: "polyline" };
    });
    onApply(newLines, { hub: hubParams, shroud: shroudParams });
  };

  const setHub    = (key) => (val) => setHubParams(p => ({ ...p, [key]: val }));
  const setShroud = (key) => (val) => setShroudParams(p => ({ ...p, [key]: val }));

  const traces = buildDiffusorAssistantTraces(hubParams, shroudParams, lines);

  return (
    <Modal open={open} onClose={onClose} title="Radial Diffusor Channel Assistant" size="2xl">
      <div className="flex flex-col" style={{ height: "640px" }}>

        <div className="flex flex-1 min-h-0 -mx-6 border-y border-gray-100">

          {/* Param table */}
          <div className="w-[480px] shrink-0 border-r border-gray-200 overflow-y-auto p-4 space-y-4">

            {/* Column headers */}
            <div className="grid grid-cols-[1fr_88px_88px] gap-x-3 px-1 pb-1 border-b border-gray-200">
              <div />
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LINE_COLORS[0] }} />
                <span className="text-xs font-bold" style={{ color: LINE_COLORS[0] }}>HUB</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LINE_COLORS[LINE_COLORS.length - 1] }} />
                <span className="text-xs font-bold" style={{ color: LINE_COLORS[LINE_COLORS.length - 1] }}>SHROUD</span>
              </div>
            </div>

            {/* Parameter diagram toggle */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowDiagram(s => !s)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span>Parameter diagram</span>
                {showDiagram
                  ? <ChevronDownIcon className="h-3.5 w-3.5" />
                  : <ChevronRightIcon className="h-3.5 w-3.5" />}
              </button>
              {showDiagram && (
                <div className="px-2 py-2 bg-white">
                  <DiffusorParamDiagram />
                </div>
              )}
            </div>

            {/* Param sections */}
            {DIFFUSOR_PARAM_SECTIONS.map(({ title, rows }) => (
              <div key={title}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  {title}
                </p>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  {rows.map(({ key, label, sub, unit }) => (
                    <div key={key}
                      className="grid grid-cols-[1fr_88px_88px] gap-x-3 px-3 py-1.5 border-b border-gray-100 last:border-0 items-center">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700">{label}</p>
                        <p className="text-xs text-gray-400 leading-tight">{sub}</p>
                      </div>
                      <ParamCell value={hubParams[key]} onChange={setHub(key)} unit={unit} readOnly={isLocked} />
                      <ParamCell value={shroudParams[key]} onChange={setShroud(key)} unit={unit} readOnly={isLocked} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Geometry legend */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-0.5">
              <p className="font-semibold text-gray-600 mb-1">Tangent lines</p>
              <p>Inlet:  z(r) = Z3 + (r − R3)·tan(Γ4)</p>
              <p>Return: z(r) = Z6 − (r − R6)·tan(Γ7)</p>
              <p className="mt-1">CP34, CP56, CP78 are derived — no manual input needed.</p>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 min-w-0 p-3 flex flex-col">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 shrink-0">
              Live preview — ○ anchor points · ◆ B-spline control points · ‥ tangent handles
            </p>
            <div className="flex-1 min-h-0">
              <Plot
                data={traces}
                layout={{
                  margin: { l: 55, r: 14, t: 10, b: 45 },
                  xaxis: { title: "r [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot" },
                  yaxis: { title: "z [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot", scaleanchor: "x", scaleratio: 1 },
                  paper_bgcolor: "transparent",
                  plot_bgcolor: "#f9fafb",
                  showlegend: true,
                  legend: { font: { size: 10 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
                  hovermode: "closest",
                  dragmode: "pan",
                }}
                config={{ displayModeBar: "hover", modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]], scrollZoom: true, responsive: true, displaylogo: false }}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 shrink-0">
          <p className="text-xs text-gray-400">
            Applies computed r/z points to every stream surface (SLs interpolated).
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleApply} disabled={isLocked}>Apply to all lines</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MECOPage() {
  const { LINE_COLORS } = useChartColors();
  const { activeDesign, saveConstructionLines, setActiveDesign } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();
  const { types: mecoTypes } = useLookups("meco_type");
  const [lines, setLines]                 = useState(DEFAULT_LINES);
  const [activeLineIdx, setActiveLineIdx] = useState(0);
  const [saving, setSaving]               = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const syncedIdRef = useRef(null);

  useEffect(() => {
    if (!activeDesign?.id) return;
    const cls = activeDesign.construction_lines;
    if (cls === undefined) return;
    if (syncedIdRef.current === activeDesign.id) return;
    setLines(cls.length > 0 ? cls : DEFAULT_LINES);
    setActiveLineIdx(0);
    syncedIdRef.current = activeDesign.id;
  }, [activeDesign]);

  const isLocked   = isReadOnly;
  const isDiffusor = mecoTypes.find(t => t.id === activeDesign?.meco_type_id)?.label === "Radial Diffusor Channel";

  const updateCoords = (idx, r, z) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, r_coords: r, z_coords: z } : l));

  const toggleActive = (idx, val) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, active: val } : l));

  const handleAssistantApply = async (newLines, assistantParams) => {
    setLines(newLines);
    setShowAssistant(false);
    // Persist the hub/shroud params so the modal reopens pre-filled
    try {
      const { data } = await bladesApi.update(activeDesign.id, {
        meco_assistant_params: assistantParams,
      });
      setActiveDesign(data);
    } catch { /* non-critical — lines are already applied */ }
  };

  const save = async () => {
    if (isLocked) return;
    setSaving(true);
    try { await saveConstructionLines(activeDesign.id, lines); }
    catch { /* 403 or network error — stay silent, UI already blocked */ }
    finally { setSaving(false); }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  const activeLine = lines[activeLineIdx];
  const chartData  = resolveChartData(lines).filter((_, i) => lines[i].active !== false);

  return (
    <div className="flex h-full">

      {/* Stream surface list */}
      <div className="w-44 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stream surfaces</p>
          <button onClick={() => setShowAssistant(true)} disabled={isLocked}
            className="flex items-center gap-1.5 text-xs font-medium text-bh-600
              hover:text-bh-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <SparklesIcon className="h-3.5 w-3.5 shrink-0" />
            {isDiffusor ? "Diffusor Assistant" : "B-Spline Assistant"}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
          {lines.map((cl, i) => (
            <button key={cl.name} onClick={() => setActiveLineIdx(i)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                ${i === activeLineIdx
                  ? "bg-bh-50 text-bh-700 border border-bh-200"
                  : "text-gray-600 hover:bg-gray-50"}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: LINE_COLORS[i % LINE_COLORS.length] }} />
              <span className="flex-1">{cl.name}</span>
              {!cl.active && <span className="text-xs text-gray-400">(off)</span>}
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-gray-200">
          <Button size="sm" className="w-full justify-center"
            onClick={save} loading={saving} disabled={isLocked} shortcut="Ctrl+S">
            Save
          </Button>
        </div>
      </div>

      {/* Coordinate table panel */}
      <div className="w-80 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: LINE_COLORS[activeLineIdx % LINE_COLORS.length] }} />
            <p className="text-sm font-semibold text-gray-800 truncate">{activeLine?.name}</p>
          </div>
          <label className={`flex items-center gap-1.5 text-xs text-gray-500 shrink-0
            ${isLocked ? "opacity-50 cursor-default" : "cursor-pointer"}`}>
            <input type="checkbox" checked={activeLine?.active ?? true}
              onChange={e => !isLocked && toggleActive(activeLineIdx, e.target.checked)}
              disabled={isLocked} className="rounded" />
            Active
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {activeLine && (
            <CoordTable
              key={`${activeLineIdx}-${activeDesign?.id}`}
              rCoords={activeLine.r_coords || []}
              zCoords={activeLine.z_coords || []}
              onChange={(r, z) => updateCoords(activeLineIdx, r, z)}
              readOnly={isLocked}
            />
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-800">Meridional Contour</p>
          <p className="text-xs text-gray-400">Active stream surfaces · r/z polyline</p>
        </div>
        <div className="flex-1 p-3">
          <MeridContour lines={chartData} />
        </div>
      </div>

      {isDiffusor ? (
        <RadialDiffusorAssistantModal
          open={showAssistant}
          onClose={() => setShowAssistant(false)}
          lines={lines}
          savedAssistantParams={activeDesign?.meco_assistant_params}
          onApply={handleAssistantApply}
          isLocked={isLocked}
        />
      ) : (
        <MecoAssistantModal
          open={showAssistant}
          onClose={() => setShowAssistant(false)}
          lines={lines}
          savedAssistantParams={activeDesign?.meco_assistant_params}
          onApply={handleAssistantApply}
          isLocked={isLocked}
        />
      )}
    </div>
  );
}
